import {
	VirtualScrollStrategy,
	CdkVirtualScrollViewport
} from '@angular/cdk/scrolling';
import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ProcessedComment } from '@go-modules/services/comment-list/comment-list.service';
import { Observable, Subject, distinctUntilChanged } from 'rxjs';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';

interface MessageHeight {
	value: number;
	source: 'predicted' | 'actual';
}

@Injectable()
export class CommentScrollStrategy implements VirtualScrollStrategy {
	private viewport!: CdkVirtualScrollViewport | null;
	private viewportWidth: number = 0;
	private comments: ProcessedComment[] = [];
	private wrapper!: ChildNode | null;

	// The number of comments to render above and below the viewport for smoother scrolling
	private readonly NumCommentsToRenderOutsideViewport = 10;
	private readonly CommentContainerVerticalPadding = 20;
	private readonly CommentContainerHorizontalPadding = 14;
	private readonly CommentHeaderHeight = 20;
	private readonly CommentRowHeight = 21;
	private readonly TimeContainerWidth = 40;
	private readonly TimeContainerMarginRightPlusLeft = 25;
	private readonly ReactionsHeight = 49;
	private readonly MarkersHeight = 44;
	private readonly AiMarkersHeight = 30;
	private readonly AudioHeight = 99;
	private readonly CommentUpdatedByHeight = 23;
	private readonly CommentExcessWidth = this.TimeContainerWidth +
		this.TimeContainerMarginRightPlusLeft +
		this.CommentContainerHorizontalPadding;

	private _scrolledIndexChange$ = new Subject<number>();
	public scrolledIndexChange: Observable<number> = this._scrolledIndexChange$.pipe(distinctUntilChanged());
	public heightCache = new Map<number, MessageHeight>();

	constructor (private sanitizer: DomSanitizer, private featureFlag: NgxFeatureFlagService) {}

	/** VirtualScrollStrategy Interface Overrides */
	public onContentRendered (): void {}
	public onRenderedOffsetChanged (): void {}

	public attach (viewport: CdkVirtualScrollViewport): void {
		this.viewport = viewport;
		this.viewportWidth = viewport.getElementRef().nativeElement.clientWidth;
		this.wrapper = viewport.getElementRef().nativeElement.childNodes[0];

		if (this.comments) {
			this.viewport.setTotalContentSize(this.getTotalHeight());
			this.updateRenderedRange();
		}
	}

	/**
	 * The scroll strategy will automatically detach when the component its' on gets destroyed
	 */
	public detach (): void {
		this.viewport = null;
		this.wrapper = null;
		this.heightCache.clear();
		this._scrolledIndexChange$.complete();
	}

	public onContentScrolled (): void {
		if (!this.viewport) return;

		this.updateRenderedRange();
	}

	public onDataLengthChanged (): void {
		if (!this.viewport) {
			return;
		}

		this.viewport.setTotalContentSize(this.getTotalHeight());
		this.updateRenderedRange();
	}

	/**
	 * Scrolls to a comment by a provided index
	 */
	public scrollToIndex (index: number, behavior: ScrollBehavior): void {
		if (!this.viewport) {
			return;
		}

		const offset = this.getOffsetByMsgIdx(index);
		this.viewport.scrollToOffset(offset, behavior);
	}
	/** VirtualScrollStrategy Interface Overrides End */

	/**
	 * Update the comments that are being rendered
	 */
	public updateComments (comments: ProcessedComment[]) {
		this.comments = comments;

		if (this.viewport) {
			this.viewport.checkViewportSize();
		}
	}

	/**
	 * Returns the predicted height of a comment element
	 */
	private predictCommentHeight = (comment: ProcessedComment): number => {
		let height = this.CommentHeaderHeight + this.CommentContainerVerticalPadding;

		if (comment.text) {
			height += this.approximateTextHeight(comment.text);
		}

		if (comment.updated_by) {
			height += this.CommentUpdatedByHeight;
		}

		if (comment.markers?.length > 0) {
			if (this.featureFlag.isAvailable('AI_CONFIG') && comment.was_ai_generated) {
				height += this.AiMarkersHeight;
			} else {
				height += this.MarkersHeight;
			}
		}

		if (comment.media?.length > 0) {
			comment.media.forEach((media) => {
				if (media.item.media_type === 'audio') {
					height += this.AudioHeight;
				} else {
					const mediaWidth = Math.max(Math.min(this.viewportWidth - this.CommentExcessWidth, 450), 268);
					const mediaHeight = mediaWidth * (3 / 4);
					const mediaMarginAndTitleHeight = 29.5;
					height += mediaHeight + mediaMarginAndTitleHeight;
				}
			});
			const gapBetweenMedia = 10;
			height += gapBetweenMedia * (comment.media.length - 1);
		}

		if (comment.reactions?.length > 0) {
			height += this.ReactionsHeight;
		}

		return height;
	};

	private approximateTextHeight (text: string): number {
		// if plain text, return approximate rows * row height
		if (!/[<>&]/.test(text)) {
			// 7px avg character width for 14px font size
			const charsPerRow = (this.viewportWidth - this.CommentExcessWidth) / 7;
			return Math.ceil(text.length / charsPerRow) * this.CommentRowHeight;
		}

		// Create a temporary div to render the text
		// people can paste in a all sorts of html that we don't even officially support (i.e. a table)
		// so we need to sactually measure the height of the text
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = this.sanitizer.sanitize(SecurityContext.HTML, text);

		tempDiv.style.width = `${this.viewportWidth - this.CommentExcessWidth}px`;
		tempDiv.style.position = 'absolute';
		tempDiv.style.visibility = 'hidden';
		tempDiv.style.overflow = 'auto';
		tempDiv.style.lineHeight = '1.5';

		document.body.appendChild(tempDiv);
		const height = tempDiv.offsetHeight;
		document.body.removeChild(tempDiv);

		return height;
	}

	/**
	 * Returns the cached or predicted height of a comment
	 */
	private getCommentHeight (comment: ProcessedComment): number {
		const cachedHeight = this.heightCache.get(Number(comment.comment_id));
		if (cachedHeight) return cachedHeight.value;

		const predictedHeight = this.predictCommentHeight(comment);
		this.heightCache.set(Number(comment.comment_id), {
			value: predictedHeight,
			source: 'predicted'
		});

		return predictedHeight;
	}

	/**
	 * Returns the total height of all supplied comments
	 */
	private measureCommentsHeight (comments: ProcessedComment[]): number {
		return comments
			.map((c) => this.getCommentHeight(c))
			.reduce((a, b) => a + b, 0);
	}

	/**
	 * Returns the total height of all comment elements (the scrollable container)
	 */
	private getTotalHeight (): number {
		return this.measureCommentsHeight(this.comments);
	}

	/**
	 * Returns the offset relative to the top of the container by a provided comment index
	 */
	private getOffsetByMsgIdx (idx: number): number {
		return this.measureCommentsHeight(this.comments.slice(0, idx));
	}

	/**
	 * Returns the comment index by a provided offset.
	 */
	private getCommentIdxByOffset (offset: number): number {
		let accumOffset = 0;

		for (let i = 0; i < this.comments.length; i++) {
			const msg = this.comments[i];
			const msgHeight = this.getCommentHeight(msg);
			accumOffset += msgHeight;

			if (accumOffset >= offset) {
				return i;
			}
		}

		return 0;
	}

	/**
	 * Returns the number of comments that can fit in the viewport
	 */
	private determineCommentsCountInViewport (startIdx: number): number {
		let totalSize = 0;
		// That is the height of the scrollable container
		const viewportSize = this.viewport.getViewportSize();

		for (let i = startIdx; i < this.comments.length; i++) {
			const msg = this.comments[i];
			totalSize += this.getCommentHeight(msg);

			if (totalSize >= viewportSize) {
				return i - startIdx + 1;
			}
		}

		return 0;
	}

	/**
	 * Gets called on every viewport update (scrolling, resizing)
	 */
	private updateRenderedRange () {
		const scrollOffset = this.viewport.measureScrollOffset();
		const scrollIdx = this.getCommentIdxByOffset(scrollOffset);
		const dataLength = this.viewport.getDataLength();
		const renderedRange = this.viewport.getRenderedRange();
		const range = {
			start: renderedRange.start,
			end: renderedRange.end
		};

		// Instruct the virtual scroll to render extra messages above and below the viewport
		// If we've reached the end, keep the start constant
		if (range.end === dataLength) {
			range.start = renderedRange.start;
		} else {
			range.start = Math.max(
				0,
				scrollIdx - this.NumCommentsToRenderOutsideViewport
			);
		}
		range.end = Math.min(
			dataLength,
			scrollIdx +
				this.determineCommentsCountInViewport(scrollIdx) +
				this.NumCommentsToRenderOutsideViewport
		);

		this.viewport.setRenderedRange(range);
		this.viewport.setRenderedContentOffset(this.getOffsetByMsgIdx(range.start));
		this._scrolledIndexChange$.next(scrollIdx);
		this.updateHeight();
	}

	/**
	 * Updates the height of each comment element while caching queried results
	 */
	private updateHeight () {
		if (!this.wrapper) {
			return;
		}

		if (
			this.viewportWidth !==
			this.viewport.getElementRef().nativeElement.clientWidth
		) {
			this.viewportWidth =
				this.viewport.getElementRef().nativeElement.clientWidth;
			this.heightCache.clear();
		}

		// Get a reference to the ul element child node
		const ulNode = this.wrapper.childNodes;
		if (ulNode == null || ulNode.length === 0) return;

		// Get the UL child nodes, this should contain li nodes
		const childUlNodes = this.wrapper.childNodes[0].childNodes;

		let cacheUpdated: boolean = false;

		// eslint-disable-next-line @typescript-eslint/prefer-for-of
		for (let i = 0; i < childUlNodes.length; i++) {
			const node = childUlNodes[i] as HTMLElement;

			// Check if the node is actually an li element
			if (node && node.nodeName === 'LI') {
				// Get the message ID
				const id = node.getAttribute('data-comment-id') as string;
				const cachedHeight = this.heightCache.get(Number(id));

				// Update the height cache, if the existing height is predicted
				if (!cachedHeight || cachedHeight.source !== 'actual') {
					const height = node.clientHeight;
					this.heightCache.set(Number(id), { value: height, source: 'actual' });
					cacheUpdated = true;
				}
			}
		}

		// Reset the total content size only if there has been a cache change
		if (cacheUpdated) {
			this.viewport.setTotalContentSize(this.getTotalHeight());
		}
	}
}
